import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  Heart,
  Pause,
  Play,
  SkipBack,
  SkipForward,
  X,
  RotateCcw,
  RotateCw,
} from 'lucide-react';
import { routeBuilders } from '../routes';
import { slugify } from '../../utils/slug';
import { usePlayerController } from './PlayerProvider';
import { usePlayerStore } from './playerStore';
import { shallow } from 'zustand/shallow';

type FullPlayerProps = {
  onToggleLike?: (seriesId: string) => void;
  isLiked?: boolean;
  onMarkComplete?: () => void;
};

const formatTime = (seconds?: number | null) => {
  if (!seconds || Number.isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export function FullPlayer({ onToggleLike, isLiked = false, onMarkComplete }: FullPlayerProps) {
  const { togglePlay, next, previous, seek, openFullView, selectEpisode } = usePlayerController();
  const queue = usePlayerStore((state) => state.queue);
  const currentEpisodeId = usePlayerStore((state) => state.currentEpisodeId);
  const currentEpisode = queue.find((ep) => ep.id === currentEpisodeId) ?? null;
  const currentTime = usePlayerStore((state) => state.currentTime);
  const duration = usePlayerStore((state) => state.duration);
  const isPlaying = usePlayerStore((state) => state.isPlaying);
  const isFullViewOpen = usePlayerStore((state) => state.isFullViewOpen);

  if (!isFullViewOpen || !currentEpisode) return null;

  const seriesPath = currentEpisode.seriesSlug
    ? routeBuilders.seriesPublic(currentEpisode.seriesSlug)
    : undefined;
  const creatorSlug = currentEpisode.creatorSlug ?? (currentEpisode.creatorName ? slugify(currentEpisode.creatorName) : null);
  const creatorPath = creatorSlug ? routeBuilders.creatorPublic(creatorSlug) : undefined;

  return (
    <div className="fixed inset-x-0 bottom-16 md:bottom-20 z-40 px-3">
      <div className="mx-auto max-w-6xl rounded-3xl border border-base-300 bg-base-100 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-base-200">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-base-content/60">Now Playing</p>
            <h3 className="font-semibold">{currentEpisode.seriesTitle}</h3>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => openFullView(false)} aria-label="Close full player">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="grid gap-6 px-5 py-6 md:grid-cols-[2fr_1fr]">
          <div className="flex flex-col gap-4">
            <div className="flex gap-4">
              <div className="h-32 w-32 rounded-2xl overflow-hidden bg-base-300 flex-shrink-0 shadow-lg">
                {currentEpisode.coverImage ? (
                  <img src={currentEpisode.coverImage} alt={currentEpisode.title} className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full bg-base-300" />
                )}
              </div>
              <div className="flex-1 min-w-0 space-y-1">
                <p className="text-xs text-base-content/60">
                  {creatorPath ? (
                    <Link to={creatorPath} className="hover:underline">
                      {currentEpisode.creatorName ?? currentEpisode.seriesTitle}
                    </Link>
                  ) : (
                    currentEpisode.creatorName ?? currentEpisode.seriesTitle
                  )}
                </p>
                <h2 className="text-xl font-semibold leading-tight truncate">{currentEpisode.title}</h2>
                {seriesPath && (
                  <Link to={seriesPath} className="text-sm text-primary hover:underline">
                    View series
                  </Link>
                )}
                <p className="text-sm text-base-content/70 line-clamp-3 mt-2">
                  {currentEpisode.description || 'No description for this episode yet.'}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="relative h-2 w-full rounded-full bg-base-200 overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-primary transition-[width]"
                  style={{ width: `${duration ? Math.min((currentTime / duration) * 100, 100) : 0}%` }}
                />
                <input
                  type="range"
                  min={0}
                  max={duration || 1}
                  step={0.5}
                  value={Math.min(currentTime, duration || currentTime)}
                  onChange={(e) => seek(Number(e.target.value))}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  aria-label="Seek"
                />
              </div>
              <div className="flex items-center justify-between text-xs text-base-content/60">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button className="btn btn-outline btn-sm" onClick={previous}>
                <SkipBack className="h-4 w-4" />
                Prev
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => seek(Math.max(0, currentTime - 15))}
              >
                <RotateCcw className="h-4 w-4" />
                -15s
              </button>
              <button className="btn btn-primary" onClick={togglePlay}>
                {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
                {isPlaying ? 'Pause' : 'Play'}
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  const target = duration ? Math.min(duration, currentTime + 15) : currentTime + 15;
                  seek(target);
                }}
              >
                +15s
                <RotateCw className="h-4 w-4" />
              </button>
              <button className="btn btn-outline btn-sm" onClick={next}>
                Next
                <SkipForward className="h-4 w-4" />
              </button>
              <button
                className={`btn btn-ghost btn-square ${isLiked ? 'text-primary' : ''}`}
                onClick={() => currentEpisode.seriesId && onToggleLike?.(currentEpisode.seriesId)}
                aria-label={isLiked ? 'Unlike series' : 'Like series'}
              >
                <Heart className={`h-5 w-5 ${isLiked ? 'fill-current' : ''}`} />
              </button>
              <button
                className="btn btn-ghost btn-square"
                onClick={() => onMarkComplete?.()}
                aria-label="Mark completed"
              >
                <CheckCircle2 className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold">Episode List</h4>
              <button className="btn btn-ghost btn-sm" onClick={() => openFullView(false)}>
                Close
              </button>
            </div>
            <div className="max-h-[260px] overflow-y-auto space-y-2 pr-1">
              {queue.map((episode, idx) => {
                const isActive = episode.id === currentEpisode.id;
                return (
                  <button
                    key={episode.id}
                    className={`w-full text-left p-3 rounded-xl border ${
                      isActive ? 'border-primary bg-primary/10' : 'border-base-300 bg-base-200/60'
                    } hover:border-primary transition`}
                    onClick={() => selectEpisode(episode.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-lg overflow-hidden bg-base-300 flex-shrink-0">
                        {episode.coverImage ? (
                          <img src={episode.coverImage} alt={episode.title} className="h-full w-full object-cover" />
                        ) : (
                          <div className="h-full w-full bg-base-300" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-base-content/60">Episode {episode.episodeNumber ?? idx + 1}</p>
                        <p className="font-medium truncate">{episode.title}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
              {queue.length === 0 && (
                <div className="text-center text-base-content/60 py-6 text-sm">Queue is empty.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
